import { apiConfigured, env } from '../config/env';
import type {
  AnalysisKind,
  AnalysisParameters,
  AnalysisRequestWire,
  AnalysisSourceWire,
  ApiErrorPayload,
  ApiLanguage,
  ApiNormalization,
  ApiTermField,
  ArtifactLinks,
  BuildSummary,
  ExportFormat,
  JobEvent,
  JobRequest,
  JobResult,
  JobSource,
  KeywordSource,
  NormalizationMethod,
  OpenAlexQuery,
  OpenAlexSourceWire,
  PublicMap,
  QuotaStatus,
  QuotaWire,
  ServiceLimits,
  TermLanguage,
  UploadReceipt,
} from './types';
import { ANALYSIS_KINDS } from './types';
import type { VosJsonDocument, VosJsonItem, VosJsonLink } from './vosjson';

/**
 * Client for the journalmap API (Cloud Run service A).
 *
 * THE CONTRACT, READ OFF THE DEPLOYED SERVICE
 * -------------------------------------------
 *   GET    /health                              -> HealthResponse           (public)
 *   GET    /v1/openalex/preview?filter=&search= -> OpenAlexPreviewResponse  (public, rate limited)
 *   GET    /v1/quota                            -> QuotaResponse            (auth)
 *   POST   /v1/uploads          multipart       -> UploadResponse           (auth)
 *   POST   /v1/analyses                         -> AnalysisResponse         (auth)
 *   GET    /v1/analyses/{id}?include_map=       -> AnalysisResponse         (auth, or public
 *                                                                           when shared)
 *   PATCH  /v1/analyses/{id}    {shared}        -> AnalysisResponse         (auth, owner)
 *   DELETE /v1/analyses/{id}                    -> 204                      (auth, owner)
 *
 * This file used to describe a different API -- `/v1/jobs`, `/v1/limits`,
 * `/v1/maps/{id}`, `/v1/openalex/count`, `/v1/public/maps/{id}` -- none of
 * which exist. Both halves were written against a prose spec instead of
 * against each other, and `request<T>()` asserted its result type rather than
 * checking it, so every one of those calls compiled and then 404'd at runtime.
 * Hence the shape of this module: nothing is cast. `requestJson` returns
 * `unknown`, and one mapper per response walks it field by field into the type
 * the components read. A field that moves on the server now shows up as a
 * wrong value from an obvious place, not as `undefined` three screens away.
 *
 * The two vocabularies are documented in `./types.ts`; every translation
 * between them lives here.
 *
 * Errors come back as `{"error": {"code", "message", "details"}}` with a
 * matching HTTP status.
 */

/** Supplied by the auth layer at startup; avoids importing Firebase here. */
type TokenProvider = (forceRefresh?: boolean) => Promise<string | null>;

let getToken: TokenProvider = async () => null;

export function setAuthTokenProvider(provider: TokenProvider): void {
  getToken = provider;
}

/** An error with a machine-readable code, so the UI can say something useful
 *  instead of printing a stack trace. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown>;

  constructor(payload: ApiErrorPayload, status = 0) {
    super(payload.message || payload.code);
    this.name = 'ApiError';
    this.code = payload.code;
    this.status = status;
    this.details = payload.details ?? {};
  }

  /** Read a numeric field out of `details` without trusting the server. */
  num(key: string): number | null {
    const value = this.details[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  str(key: string): string | null {
    const value = this.details[key];
    return typeof value === 'string' ? value : null;
  }
}

function localError(code: string, message: string, details?: Record<string, unknown>): ApiError {
  return new ApiError({ code, message, ...(details ? { details } : {}) });
}

// ---------------------------------------------------------------------------
// transport
// ---------------------------------------------------------------------------

function url(path: string): string {
  if (!apiConfigured) {
    throw localError('not_configured', 'VITE_API_BASE_URL', { name: 'VITE_API_BASE_URL' });
  }
  return `${env.apiBaseUrl}${path}`;
}

type AuthMode = 'required' | 'none';

/** Everything this module ever needs from `RequestInit`, typed rather than
 *  cast: `init.headers as Record<string, string>` is exactly the kind of
 *  unchecked bridge this rewrite exists to remove. */
interface Init {
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: string;
  headers?: Record<string, string>;
  signal?: AbortSignal | undefined;
}

async function headers(auth: AuthMode, extra?: Record<string, string>): Promise<Headers> {
  const h = new Headers(extra);
  if (auth === 'required') {
    const token = await getToken();
    if (!token) {
      throw localError('unauthenticated', 'not signed in');
    }
    h.set('Authorization', `Bearer ${token}`);
  }
  return h;
}

/** Turn any failed response into an ApiError, whatever shape the body is. */
async function toError(response: Response): Promise<ApiError> {
  let payload: ApiErrorPayload | null = null;
  try {
    const body: unknown = await response.json();
    if (isRecord(body)) {
      const raw = body['error'];
      if (isRecord(raw) && typeof raw['code'] === 'string') {
        payload = {
          code: raw['code'],
          message: typeof raw['message'] === 'string' ? raw['message'] : raw['code'],
          details: isRecord(raw['details']) ? raw['details'] : {},
        };
      }
    }
  } catch {
    // Not JSON. Fall through to a status-derived error.
  }
  if (!payload) {
    payload = { code: statusToCode(response.status), message: `HTTP ${response.status}` };
  }
  return new ApiError(payload, response.status);
}

function statusToCode(status: number): string {
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 413) return 'upload_too_large';
  if (status === 422) return 'invalid_request';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'internal';
  return 'internal';
}

/** Network failures and aborts arrive as DOMException/TypeError; normalise. */
function toTransportError(cause: unknown): ApiError {
  if (cause instanceof ApiError) return cause;
  if (cause instanceof DOMException && cause.name === 'AbortError') {
    return localError('cancelled', 'aborted');
  }
  const message = cause instanceof Error ? cause.message : String(cause);
  return localError('network', message);
}

/**
 * One request, one parsed JSON value, no type assertion.
 *
 * The return type is `unknown` on purpose. Every caller has to run the value
 * through a mapper before it can touch a field, which is the whole point: the
 * old `request<T>` handed back a `T` the server had never promised.
 */
async function requestJson(path: string, init: Init, auth: AuthMode): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(url(path), {
      method: init.method,
      ...(init.body === undefined ? {} : { body: init.body }),
      headers: await headers(auth, init.headers),
      signal: init.signal,
    });
  } catch (cause) {
    throw toTransportError(cause);
  }
  if (!response.ok) throw await toError(response);
  if (response.status === 204) return null;

  const text = await response.text();
  if (!text.trim()) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw localError('internal', 'the API answered with something that is not JSON');
  }
}

// ---------------------------------------------------------------------------
// reading a response without trusting it
// ---------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function pickRecord(source: Record<string, unknown>, key: string): Record<string, unknown> | null {
  const value = source[key];
  return isRecord(value) ? value : null;
}

function pickString(source: Record<string, unknown>, key: string, fallback = ''): string {
  const value = source[key];
  return typeof value === 'string' ? value : fallback;
}

function pickNullableString(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function pickNumber(source: Record<string, unknown>, key: string, fallback: number): number {
  const value = source[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** Null both for a missing number and for one the server sent as JSON `null`,
 *  which is how `as_jsonable` writes a NaN. The two mean the same thing here:
 *  "this build could not say". */
function pickNullableNumber(source: Record<string, unknown>, key: string): number | null {
  const value = source[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function pickBoolean(source: Record<string, unknown>, key: string, fallback = false): boolean {
  const value = source[key];
  return typeof value === 'boolean' ? value : fallback;
}

function pickStringArray(source: Record<string, unknown>, key: string): string[] {
  const value: unknown = source[key];
  if (!Array.isArray(value)) return [];
  const list: unknown[] = value;
  const out: string[] = [];
  for (const entry of list) {
    if (typeof entry === 'string') out.push(entry);
  }
  return out;
}

function pickArray(source: Record<string, unknown>, key: string): unknown[] {
  const value: unknown = source[key];
  if (!Array.isArray(value)) return [];
  const list: unknown[] = value;
  return list;
}

/** A VOSviewer `weights` / `scores` object: open-ended keys, numeric values,
 *  `null` for "no value at this attribute". */
function pickNumberMap(
  source: Record<string, unknown>,
  key: string,
): Record<string, number | null> | undefined {
  const value = pickRecord(source, key);
  if (!value) return undefined;
  const out: Record<string, number | null> = {};
  for (const [name, entry] of Object.entries(value)) {
    if (typeof entry === 'number' && Number.isFinite(entry)) out[name] = entry;
    else if (entry === null) out[name] = null;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function asAnalysisKind(value: string): AnalysisKind {
  for (const kind of ANALYSIS_KINDS) {
    if (kind === value) return kind;
  }
  // A kind this build does not know means a newer server. The value is only
  // used to pick a label, so falling back beats throwing away the whole map.
  return 'co_authorship';
}

// ---------------------------------------------------------------------------
// GET /health -> ServiceLimits
// ---------------------------------------------------------------------------

/**
 * The caps this deployment enforces.
 *
 * `/v1/limits` never existed; the caps are published on the health probe,
 * which is also where the API documents that every cap the UI states has to
 * come from the server. Note the rename: the API sends `max_map_items` and the
 * UI reads `max_items`, so the old cast produced `undefined` for the item cap
 * and the "at most {items} items" callout rendered nothing.
 *
 * A response missing a cap throws rather than defaulting to zero: `useLimits`
 * keeps its compiled-in fallback on failure, and a fallback is more useful
 * than a cap of 0 that disables the form.
 */
function toServiceLimits(payload: unknown): ServiceLimits {
  if (!isRecord(payload)) throw localError('internal', 'health returned no object');
  const limits: ServiceLimits = {
    max_documents: pickNumber(payload, 'max_documents', 0),
    max_items: pickNumber(payload, 'max_map_items', 0),
    max_upload_bytes: pickNumber(payload, 'max_upload_bytes', 0),
  };
  if (limits.max_documents <= 0 || limits.max_items <= 0 || limits.max_upload_bytes <= 0) {
    throw localError('internal', 'health did not report the service limits');
  }
  return limits;
}

export function getLimits(signal?: AbortSignal): Promise<ServiceLimits> {
  return requestJson('/health', { method: 'GET', signal }, 'none').then(toServiceLimits);
}

// ---------------------------------------------------------------------------
// GET /v1/openalex/preview -> OpenAlexCount
// ---------------------------------------------------------------------------

export interface OpenAlexCount {
  count: number;
  /** The filter expression that was counted -- and, because both go through
   *  `openAlexFilter`, exactly the one a run of this query would fetch. */
  filter: string;
  /** The free-text term that went with it. */
  search: string;
  /** The per-job document cap this deployment enforces. */
  max_documents: number;
  /** False when the whole result set is larger than that cap. The API refuses
   *  such a job outright: cursor paging would return an arbitrary slice, and a
   *  map of an arbitrary slice is a map of nothing in particular. */
  within_cap: boolean;
  /** How many works a run would actually download right now. */
  would_download: number;
  /** The server's own sentence about this query, already carrying both
   *  numbers. This is what tells a user their query is too big *before* they
   *  spend a run on it. */
  message: string;
}

function toOpenAlexCount(payload: unknown): OpenAlexCount {
  if (!isRecord(payload)) throw localError('internal', 'preview returned no object');
  return {
    count: pickNumber(payload, 'count', 0),
    filter: pickString(payload, 'filter'),
    search: pickString(payload, 'search'),
    max_documents: pickNumber(payload, 'max_documents', 0),
    within_cap: pickBoolean(payload, 'within_cap', true),
    would_download: pickNumber(payload, 'would_download', 0),
    message: pickString(payload, 'message'),
  };
}

/**
 * How many works this query matches, without downloading any of them.
 *
 * It is a GET with the filter in the query string, not a POST with a JSON
 * body: `/v1/openalex/count` was never a route. The filter is compiled from
 * the builder's structured fields by `openAlexFilter`, the same function
 * `runJob` uses, so the count and the run cannot disagree.
 */
export function countOpenAlex(query: OpenAlexQuery, signal?: AbortSignal): Promise<OpenAlexCount> {
  const params = new URLSearchParams();
  const filter = openAlexFilter(query);
  if (filter) params.set('filter', filter);
  const search = (query.search ?? '').trim();
  if (search) params.set('search', search);
  return requestJson(
    `/v1/openalex/preview?${params.toString()}`,
    { method: 'GET', signal },
    'none',
  ).then(toOpenAlexCount);
}

// ---------------------------------------------------------------------------
// compiling the query builder's fields into one OpenAlex filter expression
// ---------------------------------------------------------------------------

const ISSN = /^\d{4}-\d{3}[\dxX]$/;
const SOURCE_ID = /^(?:https?:\/\/openalex\.org\/)?(S\d+)$/i;
const COUNTRY = /^[A-Za-z]{2}$/;

/**
 * A value for a `.search` filter, with the two characters that would silently
 * change the query removed.
 *
 * A comma is OpenAlex's filter separator and a pipe is its OR operator, so
 * "Journal of X, Series B" pasted verbatim would become two filters and mean
 * something else entirely. Replacing both with a space is a no-op for a
 * word-matching `.search` filter and cannot change which filter keys are set.
 */
function searchValue(raw: string): string {
  return raw.replace(/[,|]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function unsupported(field: string, message: string): ApiError {
  return localError('unsupported_filter', message, { field });
}

/**
 * Compile `OpenAlexQuery` into the single `key:value,key2:value2` expression
 * OpenAlex takes. Returns null when the query sets no filters at all.
 *
 * The mapping is not obvious and was verified against the live index:
 *
 *   year_from/year_to  publication_year:2020-2024, or `>2019` / `<2025` for a
 *                      one-sided range. Both ends inclusive, which is what the
 *                      form says.
 *   institution        raw_affiliation_strings.search -- the only works filter
 *                      that matches affiliation text.
 *   country            authorships.institutions.country_code, two letters.
 *   open_access        is_oa.
 *   venue              see below.
 *
 * A field that cannot be expressed raises instead of being dropped. Dropping
 * it would build a map of a corpus the user did not ask for and give no sign
 * of it, which is the failure this whole project exists to avoid; raising
 * fails while they are still typing, in the count, before a run is spent.
 */
export function openAlexFilter(query: OpenAlexQuery): string | null {
  const parts: string[] = [];

  const from = typeof query.year_from === 'number' ? Math.trunc(query.year_from) : null;
  const to = typeof query.year_to === 'number' ? Math.trunc(query.year_to) : null;
  if (from !== null && to !== null) parts.push(`publication_year:${from}-${to}`);
  else if (from !== null) parts.push(`publication_year:>${from - 1}`);
  else if (to !== null) parts.push(`publication_year:<${to + 1}`);

  const venue = (query.venue ?? '').trim();
  if (venue) {
    const asSourceId = SOURCE_ID.exec(venue);
    if (ISSN.test(venue)) {
      parts.push(`primary_location.source.issn:${venue.toUpperCase()}`);
    } else if (asSourceId?.[1]) {
      parts.push(`primary_location.source.id:${asSourceId[1].toUpperCase()}`);
    } else {
      // OpenAlex publishes no display-name filter for a work's source: the
      // valid field list has primary_location.source.{id,issn,type,...} and
      // nothing that matches a title. Names have to be resolved through the
      // /sources endpoint first, and this app has no route for that.
      throw unsupported(
        'venue',
        `OpenAlex cannot filter works by a journal name. Enter the ISSN of ${venue} ` +
          `(e.g. 1588-2861) or its OpenAlex source id (e.g. S1234567), or drop the field.`,
      );
    }
  }

  if (query.open_access) parts.push('is_oa:true');

  const institution = searchValue(query.institution ?? '');
  if (institution) parts.push(`raw_affiliation_strings.search:${institution}`);

  const country = (query.country ?? '').trim();
  if (country) {
    if (!COUNTRY.test(country)) {
      throw unsupported(
        'country',
        `OpenAlex filters affiliations by two-letter country code, so ${country} is not one. ` +
          `Use TW, US, JP and so on.`,
      );
    }
    parts.push(`authorships.institutions.country_code:${country.toUpperCase()}`);
  }

  // The extra-filters box is raw OpenAlex syntax and goes through untouched,
  // commas and all. It is checked for shape only: "machine learning" typed in
  // here would reach OpenAlex as an invalid field and come back as a 502-ish
  // upstream error naming 207 valid fields, which helps nobody.
  const raw = (query.filter ?? '').trim().replace(/^,+|,+$/g, '');
  if (raw) {
    for (const clause of raw.split(',')) {
      const text = clause.trim();
      if (!text) continue;
      if (!text.includes(':')) {
        throw unsupported(
          'filter',
          `Extra filters are OpenAlex 'key:value' pairs separated by commas; ` +
            `${text} has no key. Try type:article or has_doi:true.`,
        );
      }
      parts.push(text);
    }
  }

  return parts.length > 0 ? parts.join(',') : null;
}

// ---------------------------------------------------------------------------
// GET /v1/quota -> QuotaStatus
// ---------------------------------------------------------------------------

function toQuotaWire(payload: unknown): QuotaWire {
  if (!isRecord(payload)) throw localError('internal', 'quota returned no object');
  return {
    day: pickString(payload, 'day'),
    month: pickString(payload, 'month'),
    jobs_used: pickNumber(payload, 'jobs_used', 0),
    jobs_limit: pickNumber(payload, 'jobs_limit', 0),
    jobs_remaining: pickNumber(payload, 'jobs_remaining', 0),
    documents_used: pickNumber(payload, 'documents_used', 0),
    documents_limit: pickNumber(payload, 'documents_limit', 0),
    documents_remaining: pickNumber(payload, 'documents_remaining', 0),
    day_resets_at: pickString(payload, 'day_resets_at'),
    month_resets_at: pickString(payload, 'month_resets_at'),
    max_documents_per_job: pickNumber(payload, 'max_documents_per_job', 0),
    max_items_per_map: pickNumber(payload, 'max_items_per_map', 0),
  };
}

/**
 * The daily analysis quota, plus the monthly document allowance.
 *
 * The mapping is not cosmetic. The API answers with `jobs_remaining`,
 * `jobs_limit` and `day_resets_at`; the UI asks for `remaining`, `limit` and
 * `resets_at`. Casting the response straight to `QuotaStatus` compiled fine --
 * `request<T>` asserted rather than validated -- and produced `undefined` for
 * every field the badge reads, so the header rendered the raw template
 * `剩 {remaining} / {limit}` and the "out of quota" banner never fired, because
 * `undefined <= 0` is false. Map explicitly; do not cast.
 */
export async function getQuota(signal?: AbortSignal): Promise<QuotaStatus> {
  const wire = toQuotaWire(await requestJson('/v1/quota', { method: 'GET', signal }, 'required'));
  return {
    unlimited: wire.jobs_remaining < 0 || wire.jobs_limit <= 0,
    limit: wire.jobs_limit,
    used: wire.jobs_used,
    remaining: wire.jobs_remaining,
    resets_at: wire.day_resets_at,
    window: wire.day,
    documents: {
      limit: wire.documents_limit,
      used: wire.documents_used,
      remaining: wire.documents_remaining,
      resets_at: wire.month_resets_at,
    },
  };
}

// ---------------------------------------------------------------------------
// AnalysisResponse -> JobResult / PublicMap
// ---------------------------------------------------------------------------

/** The three parts of an `AnalysisResponse`, each already checked. */
interface Envelope {
  summary: Record<string, unknown>;
  map: Record<string, unknown> | null;
  artifacts: ArtifactLinks | null;
}

function toEnvelope(payload: unknown): Envelope {
  if (!isRecord(payload)) throw localError('internal', 'the analysis response was not an object');
  const summary = pickRecord(payload, 'analysis');
  if (!summary) throw localError('internal', 'the analysis response carried no analysis');
  return {
    summary,
    map: pickRecord(payload, 'map'),
    artifacts: toArtifactLinks(pickRecord(payload, 'artifacts')),
  };
}

function toArtifactLinks(raw: Record<string, unknown> | null): ArtifactLinks | null {
  if (!raw) return null;
  const json_url = pickString(raw, 'json_url');
  const map_url = pickString(raw, 'map_url');
  const network_url = pickString(raw, 'network_url');
  if (!json_url || !map_url || !network_url) return null;
  return {
    json_url,
    map_url,
    network_url,
    expires_in_seconds: pickNumber(raw, 'expires_in_seconds', 0),
  };
}

/**
 * `MapBuild.as_dict()` -> `BuildSummary`.
 *
 * Every key is renamed: the Python side says `items`, `links`, `clusters`,
 * `items_counted`, `dropped_by_thresholds`; the result screen says `n_items`,
 * `n_links`, `n_clusters`, `n_items_counted`, `n_dropped_by_thresholds`. The
 * summary's own `n_items` / `n_links` are the fallback, because they are the
 * same numbers recorded one level up.
 *
 * `notes` is empty by design: `as_dict()` does not carry the build notes, the
 * API concatenates them into `warnings`, and the result screen already renders
 * warnings and notes as one list. Reading them here too would print each one
 * twice.
 */
function toBuildSummary(
  raw: Record<string, unknown> | null,
  summary: Record<string, unknown>,
): BuildSummary {
  const build = raw ?? {};
  return {
    n_items: pickNumber(build, 'items', pickNumber(summary, 'n_items', 0)),
    n_links: pickNumber(build, 'links', pickNumber(summary, 'n_links', 0)),
    n_clusters: pickNumber(build, 'clusters', 0),
    total_link_strength: pickNumber(build, 'total_link_strength', 0),
    n_items_counted: pickNullableNumber(build, 'items_counted'),
    n_dropped_by_thresholds: pickNumber(build, 'dropped_by_thresholds', 0),
    clustering_quality: pickNullableNumber(build, 'clustering_quality'),
    used_leidenalg: pickBoolean(build, 'used_leidenalg', false),
    notes: [],
  };
}

/** VOSviewer JSON, mapped item by item.
 *
 *  The document is the one thing here big enough to make a cast tempting --
 *  a 2,000-item map with 20,000 links -- and the one thing where junk is
 *  cheapest to survive: the renderer already treats a missing coordinate or a
 *  NaN weight as "no value". So unknown keys are dropped, wrong types are
 *  dropped, and a malformed item does not take the map down with it.
 *
 *  Dropping unknown keys means this document is what the renderer needs, not
 *  a byte-for-byte copy of what the server wrote. The byte-for-byte file is
 *  `artifacts.json_url`. */
function toVosItem(raw: unknown): VosJsonItem | null {
  if (!isRecord(raw)) return null;
  const item: VosJsonItem = {};

  const id = raw['id'];
  if (typeof id === 'string' || typeof id === 'number') item.id = id;
  for (const key of ['label', 'sublabel', 'description', 'url'] as const) {
    const value = raw[key];
    if (typeof value === 'string') item[key] = value;
  }
  for (const key of ['x', 'y', 'weight', 'normalized_weight', 'red', 'green', 'blue'] as const) {
    const value = raw[key];
    if (typeof value === 'number' && Number.isFinite(value)) item[key] = value;
  }
  // Only when the source document has them: a mapped item that invented
  // `cluster: null` would show up as a diff against the API's own map.json,
  // which the result screen offers as a download.
  if ('cluster' in raw) item.cluster = pickNullableNumber(raw, 'cluster');
  if ('score' in raw) item.score = pickNullableNumber(raw, 'score');

  const weights = pickNumberMap(raw, 'weights');
  if (weights) item.weights = weights;
  const scores = pickNumberMap(raw, 'scores');
  if (scores) item.scores = scores;

  return item;
}

function toVosLink(raw: unknown): VosJsonLink | null {
  if (!isRecord(raw)) return null;
  const source = raw['source_id'];
  const target = raw['target_id'];
  const usable = (value: unknown): value is string | number =>
    typeof value === 'string' || typeof value === 'number';
  if (!usable(source) || !usable(target)) return null;
  const link: VosJsonLink = { source_id: source, target_id: target };
  const strength = raw['strength'];
  if (typeof strength === 'number' && Number.isFinite(strength)) link.strength = strength;
  return link;
}

function toVosDocument(raw: Record<string, unknown> | null): VosJsonDocument {
  const network = raw ? pickRecord(raw, 'network') : null;
  if (!network) {
    // include_map=false, or a map whose artefact could not be read. Either
    // way the result screen has nothing to draw, so say so here rather than
    // letting the canvas fail on `undefined.items`.
    throw localError('empty_map', 'the response carried no map');
  }
  const items: VosJsonItem[] = [];
  for (const entry of pickArray(network, 'items')) {
    const item = toVosItem(entry);
    if (item) items.push(item);
  }
  const links: VosJsonLink[] = [];
  for (const entry of pickArray(network, 'links')) {
    const link = toVosLink(entry);
    if (link) links.push(link);
  }
  return { network: { items, links } };
}

/** The stem the API itself uses for a map's downloads, so an untitled map is
 *  called the same thing on screen and in the file system. */
function fallbackName(id: string): string {
  return `journalmap-${id.slice(0, 8)}`;
}

/** Only the three parameters the API reports back. The request it ran is
 *  stored server-side in full -- fixed seeds and all, so a map can be
 *  reproduced -- but `AnalysisSummary` does not publish it. */
function toParameters(summary: Record<string, unknown>): AnalysisParameters {
  const parameters: AnalysisParameters = {};
  const unit = pickNullableString(summary, 'unit');
  if (unit) parameters.unit = unit;
  const counting = pickString(summary, 'counting');
  if (counting === 'full' || counting === 'fractional') parameters.counting = counting;
  const language = pickString(summary, 'language');
  if (language === 'en' || language === 'zh') parameters.language = LANGUAGE_FROM_WIRE[language];
  return parameters;
}

function toJobResult(
  envelope: Envelope,
  extra: { source_kind: 'openalex' | 'upload' | null; duration_ms: number | null },
): JobResult {
  const summary = envelope.summary;
  const id = pickString(summary, 'id');
  const shared = pickBoolean(summary, 'shared', false);
  return {
    // There is no job resource: a run is one request, and the only id the
    // service mints is the analysis id.
    job_id: id,
    map_id: id,
    name: pickNullableString(summary, 'title') ?? fallbackName(id),
    analysis: asAnalysisKind(pickString(summary, 'analysis')),
    parameters: toParameters(summary),
    source_kind: extra.source_kind,
    created_at: pickString(summary, 'created_at'),
    duration_ms: extra.duration_ms,
    n_documents: pickNumber(summary, 'n_documents', 0),
    build: toBuildSummary(pickRecord(summary, 'build'), summary),
    warnings: pickStringArray(summary, 'warnings'),
    shared,
    // This API has no separate share id: a shared analysis is read through
    // GET /v1/analyses/{id}, by the same id its owner uses. So the share link
    // is the analysis id while sharing is on, and nothing while it is off.
    share_id: shared ? id : null,
    map: toVosDocument(envelope.map),
    artifacts: envelope.artifacts,
  };
}

function toPublicMap(envelope: Envelope): PublicMap {
  const summary = envelope.summary;
  const id = pickString(summary, 'id');
  return {
    share_id: id,
    name: pickNullableString(summary, 'title') ?? fallbackName(id),
    analysis: asAnalysisKind(pickString(summary, 'analysis')),
    created_at: pickString(summary, 'created_at'),
    n_documents: pickNumber(summary, 'n_documents', 0),
    build: toBuildSummary(pickRecord(summary, 'build'), summary),
    warnings: pickStringArray(summary, 'warnings'),
    map: toVosDocument(envelope.map),
  };
}

// ---------------------------------------------------------------------------
// reading and mutating one analysis
// ---------------------------------------------------------------------------

function analysisPath(id: string, includeMap: boolean): string {
  return `/v1/analyses/${encodeURIComponent(id)}?include_map=${includeMap ? 'true' : 'false'}`;
}

/**
 * The signed-in user's own map.
 *
 * `/v1/maps/{id}` was never a route; the analysis is the map. `include_map` is
 * explicit rather than left to the server default, because this call exists
 * precisely to get the map back after a reload.
 */
export async function getMap(mapId: string, signal?: AbortSignal): Promise<JobResult> {
  const payload = await requestJson(
    analysisPath(mapId, true),
    { method: 'GET', signal },
    'required',
  );
  // Read back from storage: the source it was built from and how long it took
  // are in the stored record but not in what the API publishes.
  return toJobResult(toEnvelope(payload), { source_kind: null, duration_ms: null });
}

/**
 * A shared map, for a reader who is not signed in.
 *
 * Same route as `getMap`, with no Authorization header. The server decides:
 * an analysis whose `shared` flag is off answers 404 to everyone but its
 * owner, and it answers 404 rather than 403 so that the existence of a private
 * map is not something an anonymous caller can probe for.
 */
export async function getPublicMap(shareId: string, signal?: AbortSignal): Promise<PublicMap> {
  const payload = await requestJson(analysisPath(shareId, true), { method: 'GET', signal }, 'none');
  return toPublicMap(toEnvelope(payload));
}

/**
 * Publish or unpublish one map.
 *
 * The response is read tolerantly: this route answers with the updated
 * `AnalysisResponse`, but a 204 or a bare summary would say the same thing,
 * and the toggle should not break over which of those the server picks. What
 * it must never do is assume success -- `shared` comes back from the server
 * when the server sent it.
 */
export async function setMapShared(
  mapId: string,
  shared: boolean,
): Promise<{ shared: boolean; share_id: string | null }> {
  const payload = await requestJson(
    `/v1/analyses/${encodeURIComponent(mapId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ shared }),
      headers: { 'Content-Type': 'application/json' },
    },
    'required',
  );

  const body = isRecord(payload) ? payload : null;
  const summary = body ? (pickRecord(body, 'analysis') ?? body) : null;
  const applied = summary ? pickBoolean(summary, 'shared', shared) : shared;
  const id = summary ? pickString(summary, 'id', mapId) : mapId;
  return { shared: applied, share_id: applied ? id : null };
}

/** Delete one map and its artefacts. 204, so there is nothing to map. */
export async function deleteMap(mapId: string): Promise<void> {
  await requestJson(`/v1/analyses/${encodeURIComponent(mapId)}`, { method: 'DELETE' }, 'required');
}

// ---------------------------------------------------------------------------
// POST /v1/uploads -> UploadReceipt
// ---------------------------------------------------------------------------

function toUploadReceipt(payload: unknown): UploadReceipt {
  if (!isRecord(payload)) throw localError('internal', 'the upload response was not an object');
  return {
    upload_id: pickString(payload, 'upload_id'),
    filename: pickString(payload, 'filename'),
    // size_bytes -> n_bytes, n_documents -> n_records: the API counts bytes
    // and documents, the dropzone shows a size and a record count.
    n_bytes: pickNumber(payload, 'size_bytes', 0),
    format: pickString(payload, 'format'),
    n_records: pickNullableNumber(payload, 'n_documents'),
    warnings: pickStringArray(payload, 'warnings'),
  };
}

/**
 * Upload an export file. Uses XMLHttpRequest rather than fetch because a big
 * WoS export takes long enough on a slow link that a real byte counter is
 * worth having, and fetch still cannot report upload progress.
 */
export function uploadExport(
  file: File,
  options: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
): Promise<UploadReceipt> {
  return new Promise<UploadReceipt>((resolve, reject) => {
    void (async () => {
      let endpoint: string;
      let token: string | null;
      try {
        endpoint = url('/v1/uploads');
        token = await getToken();
      } catch (cause) {
        reject(toTransportError(cause));
        return;
      }
      if (!token) {
        reject(localError('unauthenticated', 'not signed in'));
        return;
      }

      const form = new FormData();
      form.append('file', file, file.name);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', endpoint, true);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.responseType = 'text';

      if (options.onProgress) {
        const report = options.onProgress;
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable && event.total > 0) report(event.loaded / event.total);
        };
      }

      const onAbort = () => xhr.abort();
      options.signal?.addEventListener('abort', onAbort, { once: true });

      const finish = () => options.signal?.removeEventListener('abort', onAbort);

      xhr.onerror = () => {
        finish();
        reject(localError('network', 'upload failed'));
      };
      xhr.onabort = () => {
        finish();
        reject(localError('cancelled', 'aborted'));
      };
      xhr.onload = () => {
        finish();
        let parsed: unknown = null;
        try {
          parsed = JSON.parse(xhr.responseText);
        } catch {
          parsed = null;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(toUploadReceipt(parsed));
          } catch (cause) {
            reject(toTransportError(cause));
          }
          return;
        }
        const error = isRecord(parsed) ? parsed['error'] : null;
        const payload: ApiErrorPayload =
          isRecord(error) && typeof error['code'] === 'string'
            ? {
                code: error['code'],
                message: typeof error['message'] === 'string' ? error['message'] : error['code'],
                details: isRecord(error['details']) ? error['details'] : {},
              }
            : { code: statusToCode(xhr.status), message: `HTTP ${xhr.status}` };
        reject(new ApiError(payload, xhr.status));
      };

      xhr.send(form);
    })();
  });
}

// ---------------------------------------------------------------------------
// POST /v1/analyses: JobRequest -> AnalysisRequestWire
// ---------------------------------------------------------------------------

const LANGUAGE_TO_WIRE: Record<TermLanguage, ApiLanguage> = {
  english: 'en',
  chinese: 'zh',
};

const LANGUAGE_FROM_WIRE: Record<ApiLanguage, TermLanguage> = {
  en: 'english',
  zh: 'chinese',
};

const NORMALIZATION_TO_WIRE: Record<NormalizationMethod, ApiNormalization> = {
  none: 'none',
  association: 'association_strength',
  fractionalization: 'fractionalization',
  linlog: 'linlog_modularity',
};

/** One enum in the form, two booleans on the wire. */
const KEYWORDS_TO_WIRE: Record<KeywordSource, { author: boolean; index: boolean }> = {
  author: { author: true, index: false },
  index: { author: false, index: true },
  both: { author: true, index: true },
};

function toSourceWire(source: JobSource): AnalysisSourceWire {
  if (source.kind === 'upload') {
    // The filename travels with the upload record; the request carries the id
    // and nothing else, and `extra="forbid"` would reject the rest anyway.
    return { kind: 'upload', upload_id: source.upload_id };
  }
  const wire: OpenAlexSourceWire = { kind: 'openalex' };
  const filters = openAlexFilter(source.query);
  if (filters) wire.filters = filters;
  const search = (source.query.search ?? '').trim();
  if (search) wire.search = search;
  const max = source.query.max_results;
  if (typeof max === 'number' && Number.isFinite(max) && max >= 1) {
    wire.max_results = Math.floor(max);
  }
  return wire;
}

function refuseUnsupportedParameter(name: string, message: string): never {
  throw localError('unsupported_parameter', message, { field: name });
}

/**
 * Flatten a `JobRequest` into the body `POST /v1/analyses` accepts.
 *
 * Three kinds of work happen here, and all three were missing:
 *
 *  * **Shape.** The API's request model is flat. The frontend nests everything
 *    under `parameters`, which pydantic's `extra="forbid"` rejects outright.
 *  * **Names and values.** `language: 'english'` is `'en'`; `normalization:
 *    'association'` is `'association_strength'` and `'linlog'` is
 *    `'linlog_modularity'`; `keyword_source` is the pair `author_keywords` /
 *    `index_keywords`; `n_random_starts` and `n_iterations` are
 *    `clustering_random_starts` and `clustering_iterations`.
 *  * **Omission.** Every field is optional server-side and every default is
 *    VOSviewer's own, so a parameter the form did not set is left out rather
 *    than guessed at. `language` in particular is only sent for a term map:
 *    the API rejects a language on an analysis that reads no free text, which
 *    is exactly right and would otherwise 422 a co-authorship run whose form
 *    still had Chinese selected.
 *
 * Two controls have nowhere to go, and are refused rather than dropped. The
 * server's own schema explains why: a silently ignored parameter is a wrong
 * map that looks right.
 */
export function buildAnalysisRequest(body: JobRequest): AnalysisRequestWire {
  const p = body.parameters;
  const wire: AnalysisRequestWire = {
    source: toSourceWire(body.source),
    analysis: body.analysis,
  };

  if (p.n_terms !== undefined && p.n_terms !== null) {
    refuseUnsupportedParameter(
      'n_terms',
      'This deployment cannot cap the number of terms directly; it selects terms by ' +
        'relevance instead. Clear "number of terms" and use the relevance fraction.',
    );
  }
  if (p.cocitation_key !== undefined && p.cocitation_key !== 'matchkey') {
    refuseUnsupportedParameter(
      'cocitation_key',
      'This deployment always matches cited references on author/year/locator keys. ' +
        'Raw-string matching is not offered; set the co-citation key back to match keys.',
    );
  }

  if (p.unit) wire.unit = p.unit;
  if (p.counting) wire.counting = p.counting;

  if (p.keyword_source) {
    const keywords = KEYWORDS_TO_WIRE[p.keyword_source];
    wire.author_keywords = keywords.author;
    wire.index_keywords = keywords.index;
  }

  if (p.fields) {
    const fields: ApiTermField[] = [];
    for (const field of p.fields) {
      if (field === 'title' || field === 'abstract') fields.push(field);
    }
    if (fields.length > 0) wire.fields = fields;
  }

  // Only a term map reads free text, so only a term map may carry a language.
  if (body.analysis === 'terms' && p.language) wire.language = LANGUAGE_TO_WIRE[p.language];

  if (typeof p.binary_counting === 'boolean') wire.binary_counting = p.binary_counting;
  if (p.relevance_fraction !== undefined) wire.relevance_fraction = p.relevance_fraction;

  if (typeof p.min_occurrences === 'number') wire.min_occurrences = p.min_occurrences;
  if (typeof p.min_citations === 'number') wire.min_citations = p.min_citations;
  if (p.max_items !== undefined) wire.max_items = p.max_items;

  if (p.normalization) wire.normalization = NORMALIZATION_TO_WIRE[p.normalization];
  if (typeof p.resolution === 'number') wire.resolution = p.resolution;
  if (typeof p.min_cluster_size === 'number') wire.min_cluster_size = p.min_cluster_size;
  if (typeof p.merge_small_clusters === 'boolean') {
    wire.merge_small_clusters = p.merge_small_clusters;
  }
  if (typeof p.n_random_starts === 'number') wire.clustering_random_starts = p.n_random_starts;
  if (typeof p.n_iterations === 'number') wire.clustering_iterations = p.n_iterations;
  if (typeof p.attraction === 'number') wire.attraction = p.attraction;
  if (typeof p.repulsion === 'number') wire.repulsion = p.repulsion;
  if (typeof p.largest_component === 'boolean') wire.largest_component = p.largest_component;
  if (typeof p.seed === 'number') wire.seed = p.seed;

  const title = (body.name ?? '').trim();
  // `title` is capped at 200 characters server-side; a name built from a long
  // search string should not turn the whole run into a 422.
  if (title) wire.title = title.slice(0, 200);

  return wire;
}

/**
 * Run an analysis.
 *
 * `POST /v1/analyses` is synchronous and answers with one JSON object: the
 * finished map, the artefacts and the caller's quota, all at once. There is no
 * NDJSON stream and no job resource -- `/v1/jobs` never existed -- because the
 * whole pipeline fits inside one Cloud Run request at the current caps. That
 * is what the caps are for.
 *
 * So `onEvent` fires exactly once, with a single `progress` event, before the
 * request goes out. It is the only honest thing to report: the server says
 * nothing until it is done, and inventing a "counting -> layout -> clustering"
 * trail out of a `setTimeout` would be a progress bar that lies. The panel
 * still shows the elapsed timer, and the stage falls back to a plain
 * "Working". If a streaming endpoint is ever added, this is the one place that
 * has to change.
 */
export async function runJob(
  body: JobRequest,
  options: { onEvent?: (event: JobEvent) => void; signal?: AbortSignal } = {},
): Promise<JobResult> {
  const request = buildAnalysisRequest(body);
  const startedAt = Date.now();

  const event: JobEvent = { type: 'progress', stage: 'running' };
  options.onEvent?.(event);

  const payload = await requestJson(
    '/v1/analyses',
    {
      method: 'POST',
      body: JSON.stringify(request),
      headers: { 'Content-Type': 'application/json' },
      signal: options.signal,
    },
    'required',
  );

  return toJobResult(toEnvelope(payload), {
    source_kind: body.source.kind,
    // The API does not publish its own timing, so this is the round trip as
    // the browser saw it -- which is also the number the user waited.
    duration_ms: Date.now() - startedAt,
  });
}

// ---------------------------------------------------------------------------
// artefacts
// ---------------------------------------------------------------------------

export type MapRef = { kind: 'owned'; mapId: string } | { kind: 'public'; shareId: string };

/**
 * Fresh signed URLs for one map's three artefact files.
 *
 * They are minted per response and expire, so they are never cached: ask again
 * when one is needed. `include_map=false` keeps this from dragging the whole
 * VOSviewer document along for a download link.
 */
export async function getArtifacts(target: MapRef, signal?: AbortSignal): Promise<ArtifactLinks> {
  const id = target.kind === 'owned' ? target.mapId : target.shareId;
  const payload = await requestJson(
    analysisPath(id, false),
    { method: 'GET', signal },
    target.kind === 'owned' ? 'required' : 'none',
  );
  const artifacts = toEnvelope(payload).artifacts;
  if (!artifacts) throw localError('not_found', 'this map has no stored artefacts');
  return artifacts;
}

/**
 * Fetch one export as a Blob.
 *
 * There is no `/v1/maps/{id}/export`: the API hands out signed, time-limited
 * URLs into the results bucket instead, which is why this is two round trips.
 * Going through fetch rather than an `<a href>` keeps the bearer token out of
 * any URL -- and the signed URL out of browser history -- at the cost of
 * needing CORS on the results bucket for this origin.
 */
export async function fetchExport(
  target: MapRef,
  format: ExportFormat,
  signal?: AbortSignal,
): Promise<{ blob: Blob; filename: string }> {
  if (format === 'png') {
    // The PNG is rendered from the canvas, in the reader's own theme and view;
    // the server stores no image and never has.
    throw localError(
      'unsupported_parameter',
      'The PNG is drawn in the browser. Open the map and export it from the canvas.',
    );
  }

  const artifacts = await getArtifacts(target, signal);
  const href =
    format === 'json'
      ? artifacts.json_url
      : format === 'map'
        ? artifacts.map_url
        : artifacts.network_url;

  let response: Response;
  try {
    // No Authorization header: the signature in the URL is the authorisation,
    // and sending a bearer token to storage.googleapis.com would leak it to a
    // host that has no business with it.
    response = await fetch(href, { method: 'GET', signal });
  } catch (cause) {
    throw toTransportError(cause);
  }
  if (!response.ok) throw await toError(response);

  const blob = await response.blob();
  const filename =
    filenameFrom(response.headers.get('content-disposition')) || defaultName(format);
  return { blob, filename };
}

/** Pull the filename out of a Content-Disposition header, RFC 5987 form first. */
function filenameFrom(disposition: string | null): string {
  if (!disposition) return '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // Malformed percent-encoding; fall through to the plain form.
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1] ?? '';
}

function defaultName(format: ExportFormat): string {
  switch (format) {
    case 'json':
      return 'map.json';
    case 'map':
      return 'map.txt';
    case 'network':
      return 'network.txt';
    case 'png':
      return 'map.png';
  }
}
